// Shared enums and constants. Imported by both server and client so the two
// never drift on the string values that flow across the wire and into Mongo.

// Canonical tenant-host paths. Sign-in is Astro-served at /login/; the React
// portal (agent console + customer portal) lives under /tickets/, plus the agents'
// KB manage view at /kb/. The workspace root (/) is the Knowledge Base when it's on,
// else a redirect to /login/.
// Single source of truth so server redirects, email links, and client navigation
// can never drift. Ticket deep links stay /tickets/:id (unchanged for customers).
export const PORTAL_PATHS = Object.freeze({
  login: '/login/',
  tickets: '/tickets/',
  newTicket: '/tickets/new',
  ticket: (id) => `/tickets/${id}`,
  // Knowledge Base (docs/KB.md) — served by the Astro SSR layer at the tenant root when
  // workspace.kb.enabled. The manage view is the SPA's (the ticket console's chrome) at /kb/
  // — the SPA's second tenant route beside /tickets/ (`kb` is a reserved root slug, so no
  // category can live there). It was /tickets/kb/ until 0.18.13; those URLs 301 here
  // (legacyKbManagePath in kb/paths.js).
  kbHome: '/',
  kbSearch: '/search/',
  kbManage: '/kb/',
  kbManageCategories: '/kb/categories',
  kbManageTrash: '/kb/trash',
  kbManageTags: '/kb/tags',
  kbManageRedirects: '/kb/redirects',
  kbManageSnippets: '/kb/snippets',
  kbManageLinks: '/kb/links',
  kbManageInsights: '/kb/insights',
  // Public tag pages (/tags/, /tags/{slug}/) and the Atom feed (docs/kb-specs/P5-SPEC.md §6).
  kbTags: '/tags/',
  kbFeed: '/feed.xml',
  // MCP connector consent page (docs/kb-specs/P3-SPEC.md §7.2): /oauth/authorize 302s the
  // signed-in owner here with ?request=<id>; inside the SPA (the SW fallback allowlist
  // already covers /tickets/).
  connect: '/tickets/connect/'
});

// `/login/?signedout=1` is where a no-script sign-out lands: the KB pages' account menu signs
// out with a plain same-origin form POST to /auth/logout | /auth/agent/logout, which 303s here
// (a JSON caller — the SPA — still gets JSON). The sign-in page then does the device-side half
// of the SPA's sign-out: it drops this device's push subscription (LoginIsland).
export const SIGNED_OUT_PARAM = 'signedout';

export const TICKET_STATUS = Object.freeze({
  OPEN: 'open',
  CLOSED: 'closed'
});

// Derived from who spoke last on a ticket (see PLAN.md §5):
//   active  = the customer spoke last  → needs an agent
//   pending = an agent spoke last      → waiting on the customer
export const TICKET_SUBSTATE = Object.freeze({
  ACTIVE: 'active',
  PENDING: 'pending'
});

export const PRIORITY = Object.freeze({
  HIGH: 'high',
  MED: 'med',
  LOW: 'low'
});

export const PRIORITY_ORDER = Object.freeze([
  PRIORITY.HIGH,
  PRIORITY.MED,
  PRIORITY.LOW
]);

// Message "kinds". Only customer + agent_reply are ever shown to customers;
// note + ai_summary are agent-private and stripped server-side.
export const MESSAGE_KIND = Object.freeze({
  CUSTOMER: 'customer',
  AGENT_REPLY: 'agent_reply',
  NOTE: 'note',
  AI_SUMMARY: 'ai_summary',
  AI_ANSWER: 'ai_answer', // instant AI auto-answer (deflection) — customer-visible
  AUTO_REPLY: 'auto_reply', // auto-acknowledgment / away-mode reply — agent-authored, customer-visible
  SCHEDULED_REPLY: 'scheduled_reply' // an agent reply queued to send later — agent-only until sent
});

export const CUSTOMER_VISIBLE_KINDS = Object.freeze([
  MESSAGE_KIND.CUSTOMER,
  MESSAGE_KIND.AGENT_REPLY,
  MESSAGE_KIND.AI_ANSWER,
  MESSAGE_KIND.AUTO_REPLY
]);

// Async job states for a ticket's aiAnswer sub-doc (deflection worker queue).
export const AUTO_ANSWER_STATE = Object.freeze({ PENDING: 'pending', DONE: 'done', ERROR: 'error' });

// Async job states for a ticket's autoReply sub-doc (auto-acknowledgment / away worker queue).
export const AUTO_REPLY_STATE = Object.freeze({ PENDING: 'pending', DONE: 'done', ERROR: 'error' });

// Attachment malware-scan lifecycle. `pending` = enqueued, no verdict yet;
// `skipped` = no scanner configured server-wide (served, but re-checked). Only
// `clean` (and `skipped` while unconfigured) is servable — pending withholds.
export const SCAN_STATE = Object.freeze({
  PENDING: 'pending',
  CLEAN: 'clean',
  INFECTED: 'infected',
  ERROR: 'error',
  OVERSIZE: 'oversize',
  SKIPPED: 'skipped'
});

// Inbound message auto-translation lifecycle (message.translation.state). Always
// fail-open: on 'error' the original message body still renders.
export const TRANSLATION_STATE = Object.freeze({
  PENDING: 'pending',
  DONE: 'done',
  ERROR: 'error'
});

// What a scanner transport may report (pending/skipped are app-only states).
export const SCANNER_VERDICTS = Object.freeze([
  SCAN_STATE.CLEAN, SCAN_STATE.INFECTED, SCAN_STATE.ERROR, SCAN_STATE.OVERSIZE
]);

export const MAX_MESSAGE_ATTACHMENTS = 10;

// Object-storage providers for attachments (all S3-compatible). Order = display order
// in the "Images & Files" settings: Backblaze B2, Cloudflare R2, then AWS S3.
export const STORAGE_PROVIDERS = Object.freeze(['b2', 'r2', 's3']);

export const AGENT_ROLE = Object.freeze({
  OWNER: 'owner',
  ADMIN: 'admin', // can manage agents, ticket types, and routing
  AGENT: 'agent'
});

// Integration health keys — a workspace-level {ok,class,message,at} record per
// external integration, flipped only by PERSISTENT failures (bad key / out of
// credits / misconfig), surfaced to owners as a banner + one-time email.
export const INTEGRATION_HEALTH_KEY = Object.freeze({
  AI: 'ai',        // the workspace's AI provider (Anthropic/OpenAI/Google)
  STORAGE: 'storage', // Images & files object storage (R2/B2/S3)
  EMAIL: 'email',  // bring-your-own outbound email (SendGrid/Mailgun)
  LINEAR: 'linear' // Linear issue-tracker integration (OAuth token / webhook)
});

// Access ranking for Settings sections + admin routes: owner > admin > agent.
// `roleCan(userRole, requiredAccess)` — used on both client and server.
export const ROLE_RANK = Object.freeze({ owner: 3, admin: 2, agent: 1 });
export function roleCan(role, requiredAccess) {
  return (ROLE_RANK[role] || 0) >= (ROLE_RANK[requiredAccess] || 0);
}

export const TICKET_SOURCE = Object.freeze({
  API: 'api', // host-app backend via workspace API key
  SSO_FORM: 'sso_form', // SSO redirect into the hosted New Ticket form
  PUBLIC_FORM: 'public_form', // public verify-first form on the tenant host
  PORTAL: 'portal', // created from inside an authenticated portal session
  EMAIL: 'email', // inbound email intake (forwarded to the workspace ingest address)
  WIDGET: 'widget', // embeddable widget (public verify-first form served in the widget iframe)
  LINEAR: 'linear', // a labeled Linear issue created a ticket (Linear→ES intake)
  AGENT: 'agent' // an agent opened the ticket for a customer from the console (the opening message is the agent's)
});

// Purposes for entries in the magicTokens collection — each with its own TTL.
// Outbound webhook event names (docs/INTEGRATIONS.md §1). An endpoint with an
// empty `events` array is subscribed to all of them.
export const WEBHOOK_EVENT = Object.freeze({
  TICKET_CREATED: 'ticket.created',
  AGENT_REPLIED: 'ticket.agent_replied',
  CUSTOMER_REPLIED: 'ticket.customer_replied',
  STATUS_CHANGED: 'ticket.status_changed',
  ASSIGNED: 'ticket.assigned'
});
export const WEBHOOK_EVENTS = Object.freeze(Object.values(WEBHOOK_EVENT));

// Lifecycle of a queued webhook delivery.
export const DELIVERY_STATE = Object.freeze({
  PENDING: 'pending',
  DELIVERED: 'delivered',
  DEAD: 'dead'
});

export const MAGIC_PURPOSE = Object.freeze({
  SIGNIN: 'signin', // user-requested sign-in link (~15 min)
  REPLY_NOTIFICATION: 'reply_notification', // "an agent replied" link (~7 days)
  TICKET_VERIFY: 'ticket_verify', // public-form ticket confirmation (~24 h)
  AGENT_SIGNIN: 'agent_signin', // agent sign-in link (~15 min)
  EMAIL_CHANGE: 'email_change', // verify a pending agent email change (~60 min)
  SIGNUP: 'signup', // self-serve signup email confirmation (~24 h); no workspace yet
  SIGNUP_ESTABLISH: 'signup_establish', // one-time cross-host login after provisioning (~2 min)
  ADMIN_SIGNIN: 'admin_signin', // platform operator sign-in link (~15 min); no workspace
  AGENT_DIGEST: 'agent_digest', // daily-digest deep-link sign-in (~48 h); one-use, redirects to a filtered view
  SANDBOX_ESTABLISH: 'sandbox_establish', // one-time cross-host login into the sandbox playground (~2 min)
  LINEAR_OAUTH: 'linear_oauth' // Linear OAuth `state` — single-use, binds the callback to the connecting workspace+agent (~10 min); no email
});

// Audit-log event names.
export const AUTH_EVENT = Object.freeze({
  SSO_SUCCESS: 'sso_success',
  SSO_REJECT: 'sso_reject',
  MAGIC_REQUEST: 'magic_request',
  MAGIC_CONFIRM: 'magic_confirm',
  MAGIC_REJECT: 'magic_reject',
  AGENT_MAGIC_REQUEST: 'agent_magic_request',
  AGENT_MAGIC_CONFIRM: 'agent_magic_confirm',
  AGENT_MAGIC_REJECT: 'agent_magic_reject',
  LOGOUT: 'logout',
  TICKET_VERIFY_REQUEST: 'ticket_verify_request',
  TICKET_VERIFY_CONFIRM: 'ticket_verify_confirm',
  TICKET_VERIFY_REJECT: 'ticket_verify_reject',
  EMAIL_CHANGE_REQUEST: 'email_change_request',
  EMAIL_CHANGE_CONFIRM: 'email_change_confirm',
  EMAIL_CHANGE_REJECT: 'email_change_reject',
  ACCOUNT_RESET: 'account_reset',
  ACCOUNT_DELETE: 'account_delete',
  SIGNUP_REQUEST: 'signup_request',
  SIGNUP_CONFIRM: 'signup_confirm',
  SIGNUP_REJECT: 'signup_reject',
  SIGNUP_ESTABLISH: 'signup_establish',
  SIGNUP_ESTABLISH_REJECT: 'signup_establish_reject',
  // Workspace recall ("forgot which workspace?") — an email listing the workspaces an
  // address is associated with is sent from the signup host. A single generic event
  // (never reveals whether anything matched, to match the generic HTTP response).
  WORKSPACE_RECALL: 'workspace_recall',
  // Sandbox playground (sandbox.<root>) — owner opens a scoped test session into a
  // per-workspace sandbox sub-workspace via a one-time cross-host handoff.
  SANDBOX_OPEN: 'sandbox_open', // owner minted a handoff on their tenant host
  SANDBOX_ESTABLISH: 'sandbox_establish',
  SANDBOX_ESTABLISH_REJECT: 'sandbox_establish_reject',
  SANDBOX_LOGOUT: 'sandbox_logout',
  // platform admin console (admin.<root>) — cross-tenant operator auth + actions.
  // These rows carry workspaceId:null so they live in a platform-scoped audit
  // stream that survives the deletion of any tenant they act on.
  ADMIN_MAGIC_REQUEST: 'admin_magic_request',
  ADMIN_MAGIC_CONFIRM: 'admin_magic_confirm',
  ADMIN_MAGIC_REJECT: 'admin_magic_reject',
  ADMIN_LOGOUT: 'admin_logout',
  // Platform-operator 2FA (the operator's OWN second factor on admin.<root>).
  ADMIN_MFA_CHALLENGE: 'admin_mfa_challenge',
  ADMIN_MFA_VERIFY: 'admin_mfa_verify',
  ADMIN_MFA_REJECT: 'admin_mfa_reject',
  ADMIN_MFA_ENROLL: 'admin_mfa_enroll',
  ADMIN_MFA_DISABLE: 'admin_mfa_disable',
  ADMIN_MFA_RECOVERY_USED: 'admin_mfa_recovery_used',
  ADMIN_MFA_RESET: 'admin_mfa_reset', // env break-glass or operator reset
  PLATFORM_WORKSPACE_SUSPEND: 'platform_workspace_suspend',
  PLATFORM_WORKSPACE_UNSUSPEND: 'platform_workspace_unsuspend',
  PLATFORM_WORKSPACE_DELETE: 'platform_workspace_delete',
  PLATFORM_AGENT_DISABLE: 'platform_agent_disable',
  PLATFORM_AGENT_ENABLE: 'platform_agent_enable',
  PLATFORM_END_USER_DISABLE: 'platform_end_user_disable',
  PLATFORM_END_USER_ENABLE: 'platform_end_user_enable',
  // Read-only cross-tenant ticket view for QC/troubleshooting — audited per open
  // (the operator accessed a tenant's customer conversation content).
  PLATFORM_TICKET_VIEWED: 'platform_ticket_viewed',
  // Force sign-out: an operator revoked a user's active sessions WITHOUT disabling
  // the account (incident response / unwedge). Distinct from *_DISABLE.
  PLATFORM_SESSIONS_REVOKED: 'platform_sessions_revoked',
  // Operator billing actions on a tenant's Stripe subscription (money-adjacent).
  // REFUND is audited FAIL-CLOSED (the intent row is written before Stripe is called,
  // so a refund is never issued without a durable record). meta carries the target
  // workspace id + slug + amounts/ids + the operator's free-text reason.
  PLATFORM_BILLING_REFUND: 'platform_billing_refund',
  PLATFORM_BILLING_COUPON_APPLY: 'platform_billing_coupon_apply',
  PLATFORM_BILLING_COUPON_REMOVE: 'platform_billing_coupon_remove',
  // Operator edits to the subdomain denylist overlay + per-workspace overrides, and a
  // force-reassign of a workspace's subdomain (e.g. after restricting a word it used).
  PLATFORM_SLUG_RULE_ADD: 'platform_slug_rule_add',
  PLATFORM_SLUG_RULE_REMOVE: 'platform_slug_rule_remove',
  PLATFORM_SUBDOMAIN_REASSIGN: 'platform_subdomain_reassign',
  // An operator released one of a workspace's previous subdomains: its old host stops
  // redirecting and the slug becomes claimable again.
  PLATFORM_PREVIOUS_SLUG_RELEASE: 'platform_previous_slug_release',
  // An agent linked/unlinked a customer's external account id (manual verification).
  // Records old+new so a mis-link (which could collide with an SSO identity) is
  // reconstructible and reversible.
  CUSTOMER_ACCOUNT_LINKED: 'customer_account_linked',
  // An externalId-keyed upsert (SSO/API) found the incoming email already owned by a
  // DIFFERENT customer than the externalId match — the tell-tale of a mis-linked
  // account id. upsertEndUser refuses to move the email (which would hijack the other
  // customer's record or trip the unique index and lock them out), signs the session
  // into the email-matched identity, and drops this row so an operator can undo the
  // mis-link. meta carries both endUser ids + the externalId for reconstruction.
  CUSTOMER_IDENTITY_CONFLICT: 'customer_identity_conflict',
  // Identity switch between an agent/owner and their SAME-email customer identity in
  // the same workspace (one identity per browser). CUSTOMER = agent→customer: the
  // agent session is dropped and a portal session minted (a safe assurance downgrade).
  // AGENT_REQUEST = customer→agent: only an agent magic link is sent — it NEVER mints
  // an agent session (the real magic + mandatory-2FA flow does), so a low-assurance
  // customer session can't be promoted across the 2FA boundary.
  IDENTITY_SWITCH_CUSTOMER: 'identity_switch_customer',
  IDENTITY_SWITCH_AGENT_REQUEST: 'identity_switch_agent_request',
  // Passkey identity switch (customer → agent). The verified event is emitted from the
  // ONLY customer→agent code path allowed to mint an agent session (auth/switch-passkey.js),
  // and only after a UV WebAuthn assertion bound to the caller's own same-email agent.
  IDENTITY_SWITCH_AGENT_PASSKEY: 'identity_switch_agent_passkey',
  IDENTITY_SWITCH_AGENT_PASSKEY_REJECT: 'identity_switch_agent_passkey_reject',
  // Agent/owner two-factor auth (prompted after the magic link — factor #1 is email
  // possession). CHALLENGE = magic link confirmed but a 2nd factor is required (no
  // session yet); VERIFY = 2nd factor passed → full session; REJECT = bad/expired
  // factor or challenge (with reason). ENROLL/DISABLE/RECOVERY_USED manage methods;
  // *_RESET clears a locked-out agent's 2FA (owner or platform admin).
  AGENT_MFA_CHALLENGE: 'agent_mfa_challenge',
  AGENT_MFA_VERIFY: 'agent_mfa_verify',
  AGENT_MFA_REJECT: 'agent_mfa_reject',
  AGENT_MFA_ENROLL: 'agent_mfa_enroll',
  AGENT_MFA_DISABLE: 'agent_mfa_disable',
  AGENT_MFA_RECOVERY_USED: 'agent_mfa_recovery_used',
  AGENT_MFA_RESET: 'agent_mfa_reset', // reset by the workspace owner
  PLATFORM_AGENT_MFA_RESET: 'platform_agent_mfa_reset', // reset by a platform operator
  AGENT_EMAIL_CHANGED: 'agent_email_changed', // admin/owner changed an agent's email (unverified admin path)
  // Email suppression + customer erasure (PII tier — carries the affected address).
  EMAIL_UNSUBSCRIBE: 'email_unsubscribe',
  EMAIL_RESUBSCRIBE: 'email_resubscribe',
  EMAIL_SUPPRESSION_WEBHOOK: 'email_suppression_webhook',
  CUSTOMER_SELF_DELETE: 'customer_self_delete',
  // Linear integration (owner connects/disconnects a Linear org to the workspace).
  // Rows carry who connected which org for reconstruction after a mis-link/revoke.
  LINEAR_CONNECTED: 'linear_connected',
  LINEAR_DISCONNECTED: 'linear_disconnected',
  // Passkey first-factor sign-in (WebAuthn, skipping the magic link). SIGNIN on
  // success; SIGNIN_REJECT carries a reason (no_challenge / unknown_credential /
  // user_handle_mismatch / subject_inactive / bad_assertion /
  // passkey_counter_regression / counter_cas_failed / disabled_feature).
  // SECURITY_POLICY_CHANGED records a requireMfa/requirePasskey flip (old→new) —
  // policy changes are security-relevant and previously left only PII-free telemetry.
  PASSKEY_SIGNIN: 'passkey_signin',
  PASSKEY_SIGNIN_REJECT: 'passkey_signin_reject',
  SECURITY_POLICY_CHANGED: 'security_policy_changed',
  // Customer passkeys (portal self-service) + operator reset of a customer's passkeys.
  CUSTOMER_PASSKEY_ENROLL: 'customer_passkey_enroll',
  CUSTOMER_PASSKEY_REMOVE: 'customer_passkey_remove',
  PLATFORM_CUSTOMER_PASSKEY_RESET: 'platform_customer_passkey_reset'
});

// Second-factor methods an agent can use at sign-in (spec: 2FA epic). No email —
// the magic link already proves email possession.
export const MFA_METHOD = Object.freeze({
  TOTP: 'totp',
  PASSKEY: 'passkey',
  RECOVERY: 'recovery'
});

// Who performed a tracked action (events.actorType). Mirrors the four identity
// modes plus system (workers/internal) and anonymous (unauthenticated).
export const ACTOR_TYPE = Object.freeze({
  AGENT: 'agent',
  END_USER: 'endUser',
  API_KEY: 'apiKey',
  SYSTEM: 'system',
  ANONYMOUS: 'anonymous',
  PLATFORM_ADMIN: 'platformAdmin' // cross-tenant operator (admin.<root>)
});

// Product/usage telemetry event types (the `events` collection + POST /api/activity).
// CLOSED enum — track() rejects anything not here and the client beacon drops
// unknowns, so a bug can never pollute the taxonomy. Dotted, past-tense names.
// PII-free by rule: props carry ids + enums only; emails/ips/UAs live in
// authAuditLog, never here. Server emits what it can observe; the client beacon
// emits ONLY the ui.* / page.* / app.error actions the server can't see.
export const EVENT = Object.freeze({
  // tickets (emitted at the service layer — every intake path emits once)
  TICKET_CREATED: 'ticket.created',
  TICKET_AGENT_REPLIED: 'ticket.agent_replied',
  TICKET_NOTE_ADDED: 'ticket.note_added',
  TICKET_CUSTOMER_REPLIED: 'ticket.customer_replied',
  TICKET_STATUS_CHANGED: 'ticket.status_changed',
  TICKET_ASSIGNED: 'ticket.assigned',
  TICKET_PRIORITY_CHANGED: 'ticket.priority_changed',
  TICKET_TYPE_CHANGED: 'ticket.type_changed',
  TICKET_VIEWED: 'ticket.viewed',
  TICKET_AI_SUMMARIZED: 'ticket.ai_summarized',
  TICKET_AI_REPLY_GENERATED: 'ticket.ai_reply_generated',
  TICKET_AI_CLASSIFIED: 'ticket.ai_classified',
  TICKET_AI_AUTO_ANSWERED: 'ticket.ai_auto_answered',
  TICKET_AUTO_REPLIED: 'ticket.auto_replied',
  TICKET_SNOOZED: 'ticket.snoozed',
  TICKET_REPLY_SCHEDULED: 'ticket.reply_scheduled',
  TICKET_SCHEDULED_REPLY_SENT: 'ticket.scheduled_reply_sent',
  TICKET_SCHEDULED_REPLY_CANCELED: 'ticket.scheduled_reply_canceled',
  AI_BUDGET_REACHED: 'ai.budget_reached',
  TICKET_ENRICHED: 'ticket.enriched',
  // auth / sessions
  SESSION_SIGNIN: 'session.signin',
  SESSION_LOGOUT: 'session.logout',
  AGENT_DIGEST_SENT: 'agent.digest_sent',
  // self-serve signup (workspaceId may be null — the signup host is tenant-less)
  SIGNUP_REQUESTED: 'signup.requested',
  SIGNUP_CONFIRMED: 'signup.confirmed',
  SIGNUP_ESTABLISHED: 'signup.established',
  // webhooks
  WEBHOOK_ENDPOINT_CREATED: 'webhook.endpoint_created',
  WEBHOOK_ENDPOINT_UPDATED: 'webhook.endpoint_updated',
  WEBHOOK_ENDPOINT_DELETED: 'webhook.endpoint_deleted',
  WEBHOOK_SECRET_ROTATED: 'webhook.secret_rotated',
  WEBHOOK_ENDPOINT_TESTED: 'webhook.endpoint_tested',
  WEBHOOK_DELIVERY_DEAD: 'webhook.delivery_dead',
  // admin / settings
  SETTINGS_UPDATED: 'settings.updated',
  AGENT_CREATED: 'agent.created',
  AGENT_UPDATED: 'agent.updated',
  AGENT_REMOVED: 'agent.removed',
  TYPE_CREATED: 'type.created',
  TYPE_UPDATED: 'type.updated',
  TYPE_REMOVED: 'type.removed',
  API_KEY_CREATED: 'api_key.created',
  API_KEY_REVOKED: 'api_key.revoked',
  CUSTOM_HOST_ADDED: 'custom_host.added',
  CUSTOM_HOST_VERIFIED: 'custom_host.verified',
  CUSTOM_HOST_UNVERIFIED: 'custom_host.unverified',
  CUSTOM_HOST_REMOVED: 'custom_host.removed',
  SIGNING_SECRET_ROTATED: 'signing_secret.rotated',
  SIGNING_SECRET_RETIRED: 'signing_secret.retired',
  // integration health (a persistent AI / storage / email failure surfaced to the owner)
  INTEGRATION_UNHEALTHY: 'integration.unhealthy',
  INTEGRATION_RECOVERED: 'integration.recovered',
  // account
  ACCOUNT_EXPORTED: 'account.exported',
  ACCOUNT_RESET: 'account.reset',
  ACCOUNT_DELETED: 'account.deleted',
  // attachments
  ATTACHMENT_UPLOADED: 'attachment.uploaded',
  ATTACHMENT_SCAN_COMPLETED: 'attachment.scan_completed',
  ATTACHMENT_RESCAN_REQUESTED: 'attachment.rescan_requested',
  // platform admin (admin.<root>) — attributed to the TARGET workspace so the
  // action shows in that tenant's timeline; the operator's identity stays in
  // authAuditLog only (events is PII-free). NOT added to CLIENT_EVENTS.
  PLATFORM_SIGNIN: 'platform.signin',
  PLATFORM_WORKSPACE_SUSPENDED: 'platform.workspace_suspended',
  PLATFORM_WORKSPACE_UNSUSPENDED: 'platform.workspace_unsuspended',
  PLATFORM_WORKSPACE_DELETED: 'platform.workspace_deleted',
  PLATFORM_USER_DISABLED: 'platform.user_disabled',
  PLATFORM_USER_ENABLED: 'platform.user_enabled',
  PLATFORM_SESSIONS_REVOKED: 'platform.sessions_revoked',
  PLATFORM_BILLING_REFUNDED: 'platform.billing_refunded',
  PLATFORM_BILLING_COUPON_APPLIED: 'platform.billing_coupon_applied',
  PLATFORM_BILLING_COUPON_REMOVED: 'platform.billing_coupon_removed',
  PLATFORM_SUBDOMAIN_REASSIGNED: 'platform.subdomain_reassigned',
  // Email suppression / comms prefs / erasure / agent mute (PII-free telemetry).
  EMAIL_UNSUBSCRIBED: 'email.unsubscribed',
  EMAIL_SUPPRESSION_ADDED: 'email.suppression_added',
  EMAIL_PREFS_UPDATED: 'email.prefs_updated',
  CUSTOMER_SELF_DELETED: 'customer.self_deleted',
  AGENT_NOTIFICATIONS_MUTED: 'agent.notifications_muted',
  // Linear integration (PII-free telemetry — ids/enums only). connect/disconnect,
  // an ES ticket linked/unlinked to a Linear issue, a reply/comment crossing the
  // boundary, a Linear-labeled issue creating a ticket, and a rejected webhook.
  LINEAR_CONNECTED: 'linear.connected',
  LINEAR_DISCONNECTED: 'linear.disconnected',
  LINEAR_ISSUE_LINKED: 'linear.issue_linked',
  LINEAR_ISSUE_UNLINKED: 'linear.issue_unlinked',
  LINEAR_REPLY_POSTED: 'linear.reply_posted',
  LINEAR_COMMENT_SYNCED: 'linear.comment_synced',
  LINEAR_TICKET_CREATED: 'linear.ticket_created',
  LINEAR_WEBHOOK_REJECTED: 'linear.webhook_rejected',
  // Passkey (WebAuthn) — PII-free telemetry. Enrol/remove carry role + authenticator
  // traits; a passkey sign-in rides SESSION_SIGNIN's `method:'passkey'`.
  PASSKEY_ENROLLED: 'passkey.enrolled',
  PASSKEY_REMOVED: 'passkey.removed',
  PASSKEY_RESET: 'passkey.reset',
  // Knowledge Base (docs/KB.md) — ids/enums only; search telemetry never carries terms.
  KB_CATEGORY_CREATED: 'kb.category_created',
  KB_CATEGORY_UPDATED: 'kb.category_updated',
  KB_CATEGORY_DELETED: 'kb.category_deleted',
  KB_ARTICLE_CREATED: 'kb.article_created',
  KB_ARTICLE_UPDATED: 'kb.article_updated',
  KB_ARTICLE_PUBLISHED: 'kb.article_published',
  KB_ARTICLE_UNPUBLISHED: 'kb.article_unpublished',
  KB_ARTICLE_ARCHIVED: 'kb.article_archived',
  KB_ARTICLE_RESTORED: 'kb.article_restored',
  KB_ARTICLE_DELETED: 'kb.article_deleted',
  KB_ARTICLE_REVERTED: 'kb.article_reverted',
  KB_ARTICLE_MOVED: 'kb.article_moved',
  KB_MEDIA_UPLOADED: 'kb.media_uploaded',
  KB_SETTINGS_UPDATED: 'kb.settings_updated',
  KB_SEARCHED: 'kb.searched',
  KB_REDIRECT_CREATED: 'kb.redirect_created',
  // Reader "was this helpful?" vote (anonymous; props: articleId, helpful, reason, changed —
  // never the IP or its hash) and a finished AI meta description (docs/kb-specs/P4-SPEC.md).
  KB_FEEDBACK: 'kb.feedback',
  KB_DESCRIPTION_GENERATED: 'kb.description_generated',
  // Management polish (docs/kb-specs/P5-SPEC.md) — counts/ids only. KB_SEARCH_TERMS_PURGED is
  // the owner deleting (or turning off) opt-in search-term collection; terms never ride events.
  KB_BULK_ACTION: 'kb.bulk_action',
  KB_SNIPPET_CONVERTED: 'kb.snippet_converted',
  KB_SNIPPET_DELETED: 'kb.snippet_deleted',
  KB_TAG_RENAMED: 'kb.tag_renamed',
  KB_TAG_REMOVED: 'kb.tag_removed',
  KB_LINK_CHECK: 'kb.link_check',
  KB_TRASH_SWEPT: 'kb.trash_swept',
  KB_MEDIA_GC: 'kb.media_gc',
  KB_SEARCH_TERMS_PURGED: 'kb.search_terms_purged',
  // Deflection unification (docs/kb-specs/P6-SPEC.md) — an agent opened Manage → Insights
  // (props: { days } only; tracked on a ticket-insights cache miss).
  KB_INSIGHTS_VIEWED: 'kb.insights_viewed',
  // Multilingual KB (docs/kb-specs/P7-SPEC.md) — ids/locales/enums only.
  KB_TRANSLATION_CREATED: 'kb.translation_created',
  KB_TRANSLATION_GENERATED: 'kb.translation_generated',
  KB_TRANSLATION_ACKED: 'kb.translation_acked',
  KB_LANGUAGES_UPDATED: 'kb.languages_updated',
  // WordPress → KB importer (docs/kb-specs/WP-IMPORT-SPEC.md §9.5) — counts/enums only, never a
  // host, URL or title.
  KB_IMPORT_PLANNED: 'kb.import_planned',
  KB_IMPORT_STARTED: 'kb.import_started',
  KB_IMPORT_FINISHED: 'kb.import_finished',
  KB_IMPORT_CANCELLED: 'kb.import_cancelled',
  KB_IMPORT_FAILED: 'kb.import_failed',
  // MCP connector (docs/kb-specs/P3-SPEC.md) — ids/enums only (never client names).
  MCP_CONNECTED: 'mcp.connected',
  MCP_DISCONNECTED: 'mcp.disconnected',
  MCP_REVERTED: 'mcp.reverted',
  // client UI (POST /api/activity beacon — the server can't observe these)
  PAGE_VIEWED: 'page.viewed',
  UI_NEW_TICKET_OPENED: 'ui.new_ticket_opened',
  UI_SETTINGS_OPENED: 'ui.settings_opened',
  UI_NOTIFICATIONS_OPENED: 'ui.notifications_opened',
  UI_TEMPLATE_USED: 'ui.template_used',
  UI_SEARCH: 'ui.search',
  UI_THEME_CHANGED: 'ui.theme_changed',
  UI_LOCALE_CHANGED: 'ui.locale_changed',
  UI_KB_SUGGESTION_OPENED: 'ui.kb_suggestion_opened',
  UI_KB_LINK_INSERTED: 'ui.kb_link_inserted',
  APP_ERROR: 'app.error'
});
export const EVENTS = Object.freeze(Object.values(EVENT));

// The subset the client beacon (POST /api/activity) is allowed to emit — the
// server rejects any server-observable event arriving from the client.
export const CLIENT_EVENTS = Object.freeze([
  EVENT.PAGE_VIEWED, EVENT.UI_NEW_TICKET_OPENED, EVENT.UI_SETTINGS_OPENED,
  EVENT.UI_NOTIFICATIONS_OPENED, EVENT.UI_TEMPLATE_USED, EVENT.UI_SEARCH,
  EVENT.UI_THEME_CHANGED, EVENT.UI_LOCALE_CHANGED, EVENT.UI_KB_SUGGESTION_OPENED,
  EVENT.UI_KB_LINK_INSERTED, EVENT.APP_ERROR
]);

// MCP connector OAuth scopes (docs/kb-specs/P3-SPEC.md). kb:write and kb:publish each
// imply kb:read; both are offered only while MCP_WRITES_ENABLED is on.
// `tickets:insights` = aggregated ticket themes, opt-in per workspace
// (`kb.insights.supportThemes`), read-only, never implies or is implied by another scope
// (docs/kb-specs/P6-SPEC.md §4.3.1). `tickets:read` = read tickets, threads, customers and
// attachments; `tickets:write` = internal notes + ticket fields (never a customer reply),
// implies tickets:read and is offered only while MCP_WRITES_ENABLED is on
// (docs/kb-specs/MCP-TICKETS-SPEC.md). Each new scope is appended LAST so `const [READ,
// WRITE, PUBLISH, INSIGHTS] = MCP_SCOPES` keeps working; new code names them via MCP_SCOPE.
export const MCP_SCOPES = Object.freeze(['kb:read', 'kb:write', 'kb:publish', 'tickets:insights', 'tickets:read', 'tickets:write']);
export const MCP_SCOPE = Object.freeze({
  KB_READ: 'kb:read',
  KB_WRITE: 'kb:write',
  KB_PUBLISH: 'kb:publish',
  TICKETS_INSIGHTS: 'tickets:insights',
  TICKETS_READ: 'tickets:read',
  TICKETS_WRITE: 'tickets:write'
});

// ── Knowledge Base (docs/KB.md) ─────────────────────────────────────────────
// `kbArticles` holds BOTH legacy deflection snippets (retrieval-only, never rendered)
// and KB articles (rendered at the tenant root when workspace.kb.enabled).
export const KB_KIND = Object.freeze({ SNIPPET: 'snippet', ARTICLE: 'article' });
export const KB_STATUS = Object.freeze({ DRAFT: 'draft', PUBLISHED: 'published', ARCHIVED: 'archived' });
export const KB_STATUSES = Object.freeze(Object.values(KB_STATUS));
// Where an article's meta description came from; `manual` always wins and is never
// overwritten by the auto/AI derivations.
export const KB_DESCRIPTION_SOURCE = Object.freeze({ AUTO: 'auto', AI: 'ai', MANUAL: 'manual' });
export const KB_LIMITS = Object.freeze({
  MAX_ITEMS: 500,            // per-workspace cap on snippets + non-archived articles (union)
  MAX_CATEGORIES: 300,
  MAX_DEPTH: 3,              // /{c1}/{c2}/{c3}/
  SLUG_MAX: 80,
  CATEGORY_NAME_MAX: 80,
  CATEGORY_DESCRIPTION_MAX: 300,
  ARTICLE_TITLE_MAX: 160,
  DESCRIPTION_MAX: 300,      // manual meta description (auto/AI are trimmed to ~160)
  AUTO_DESCRIPTION_MAX: 160,
  TAGS_MAX: 10,
  TAG_MAX: 40,
  LEXICAL_MAX_BYTES: 400_000,
  REVISIONS_KEEP: 50,
  KB_NAME_MAX: 80,
  KB_DESCRIPTION_MAX: 300,   // the KB home's meta description (settings; the default edition)
  AI_GUIDANCE_MAX: 2_000,
  SEARCH_QUERY_MAX: 200,
  EDITORS_MAX: 200,
  BYLINE_NAME_MAX: 80,
  REDIRECT_URL_MAX: 500,
  MEDIA_PER_ARTICLE: 50,     // uploaded images + files per article (POST /api/kb/media)
  IMAGE_CAPTION_MAX: 300,    // a kb-image caption (one line; shared/src/kb/links.js MAX_IMAGE_CAPTION mirrors it)
  // Image uploads a browser can render on a public page — no HEIC (Safari-only).
  MEDIA_IMAGE_EXTENSIONS: Object.freeze(['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif']),
  // AI meta descriptions (P4): a hard count cap per workspace per UTC month, the shortest
  // reply accepted, and how much of the article's plain text goes to the provider.
  AI_DESCRIPTIONS_PER_MONTH: 300,
  AI_DESCRIPTION_MIN_CHARS: 40,
  AI_DESCRIPTION_INPUT_CHARS: 6_000,
  FEEDBACK_TTL_DAYS: 400,    // kbFeedback votes expire (TTL index kb_feedback_ttl)
  // Reading time: shown from this many words, at this many words per minute.
  READ_TIME_MIN_WORDS: 150,
  READ_TIME_WPM: 200,
  // Management polish (P5). Trash: archived articles are purged this many days after
  // `archivedAt` (hourly sweep). Media GC: kb uploads no body references are deleted once
  // this old, and referenced ones are re-checked this often.
  TRASH_RETENTION_DAYS: 30,
  MEDIA_GC_MIN_AGE_HOURS: 24,
  MEDIA_GC_RECHECK_DAYS: 7,
  BULK_MAX: 100,             // ids per POST /api/kb/bulk
  SNIPPET_CONVERT_MAX: 50,   // ids per POST /api/kb/snippets/convert
  // Link checker: distinct external URLs per run, per-request timeout, redirect hops
  // followed, how long a URL result is reused, and the minimum gap between runs.
  LINK_CHECK_MAX_URLS: 500,
  LINK_CHECK_TIMEOUT_MS: 8_000,
  LINK_CHECK_MAX_REDIRECTS: 3,
  LINK_CHECK_CACHE_HOURS: 24,
  LINK_CHECK_MIN_INTERVAL_S: 600,
  LINK_REPORT_MAX_ITEMS: 2_000,
  // Opt-in search-term analytics: stored term length, default + allowed retention (days).
  SEARCH_TERM_MAX: 100,
  SEARCH_TERMS_RETENTION_DAYS: 90,
  SEARCH_TERMS_RETENTION_CHOICES: Object.freeze([30, 90, 180]),
  INSIGHTS_ROWS: 50,         // rows per Insights table
  TAG_PAGE_INDEX_MIN: 3,     // a /tags/{slug}/ page is indexable from this many published articles
  FEED_ITEMS: 20,            // entries in /feed.xml
  // Web image variant (P6 §4.4): KB images are re-encoded to WebP at most this many px on
  // the longer edge; a webp/avif original at or under WEB_IMAGE_SKIP_BYTES is served as is.
  WEB_IMAGE_MAX_PX: 1600,
  WEB_IMAGE_SKIP_BYTES: 200 * 1024,
  // Multilingual KB (P7 §1a): additional languages a workspace may turn on (every
  // non-default supported locale), the AI translation count cap per workspace per UTC
  // month, the markdown chars sent to the provider, and the provider's output budget.
  LANGUAGES_MAX: 8,
  AI_TRANSLATIONS_PER_MONTH: 200,
  AI_TRANSLATION_INPUT_CHARS: 40_000,
  AI_TRANSLATION_MAX_OUTPUT_TOKENS: 16_000
});
// kbArticles.translationJob.state — the AI translation worker's doc-as-queue states (P7 §2.1).
export const KB_TRANSLATION_JOB = Object.freeze({
  PENDING: 'pending', DONE: 'done', ERROR: 'error', SKIPPED: 'skipped'
});
// How a translation row's current text got there (kbArticles.translation.via).
export const KB_TRANSLATION_VIA = Object.freeze({ AI: 'ai', MANUAL: 'manual', COPY: 'copy' });
// POST /api/kb/articles/:id/translations { mode } (P7 §3.2).
export const KB_TRANSLATION_MODES = Object.freeze(['ai', 'copy', 'blank']);
// The hreflang value of a page's language-neutral alternate (P7 §5.2).
export const KB_X_DEFAULT = 'x-default';
// Ticket insights (P6 §4.2) — Manage → Insights ticket blocks + the MCP get_support_themes
// tool. MCP_WINDOW_* bound the tool's `days` (the REST range stays P5's 30/90/180);
// SUBJECT_SCAN_MAX caps the subjects find; CACHE_TTL_MS = the in-process ticket-block cache.
export const KB_INSIGHTS = Object.freeze({
  MCP_WINDOW_DAYS_DEFAULT: 90,
  MCP_WINDOW_DAYS_MIN: 7,
  MCP_WINDOW_DAYS_MAX: 180,
  // A subject reaches a third-party model (get_support_themes) only when this many tickets
  // share it — a redacted-but-verbatim single customer's subject never leaves the tenant. The
  // in-app Insights panel (agents of the workspace) keeps every count. = SUBJECT_MIN of the
  // recurring-subject suggestions (P6 verify H2).
  MCP_SUBJECT_MIN: 3,
  TYPES_MAX: 50,
  SUBJECTS_MAX: 50,
  SUBJECTS_PANEL: 25,
  SUBJECT_CHARS: 120,
  SUBJECT_SCAN_MAX: 5_000,
  TOP_ARTICLES: 20,
  ZERO_TERMS_MAX: 25,
  SUGGESTIONS_MAX: 10,
  CACHE_TTL_MS: 60_000
});
// POST /api/kb/bulk actions (P5 §3.2). `delete` = permanent (admin; trashed rows only).
// `translate` (P7 §3.2) = add a language edition of each article (params { language, mode }).
export const KB_BULK_ACTIONS = Object.freeze([
  'publish', 'unpublish', 'archive', 'restore', 'delete', 'move', 'addTags', 'removeTags', 'translate'
]);
// Link-checker result per link (kbLinkReports.items[].status, kbLinkCache.status).
export const KB_LINK_STATUS = Object.freeze({
  OK: 'ok',
  REDIRECTED: 'redirected',
  BROKEN: 'broken',
  UNREACHABLE: 'unreachable',
  BLOCKED: 'blocked',          // resolves to a private/reserved address — never fetched
  MISSING: 'missing',          // kb:article: target / site path that doesn't exist
  UNPUBLISHED: 'unpublished',  // a published article links to a draft
  TRASHED: 'trashed',          // the target is in the trash
  SKIPPED: 'skipped'           // not checked (non-http, over the URL cap, time cap)
});
// kbLinkReports.state — one doc per workspace, replaced per run.
export const KB_LINK_REPORT_STATE = Object.freeze({
  PENDING: 'pending', RUNNING: 'running', DONE: 'done', ERROR: 'error'
});
// WordPress → KB importer (docs/kb-specs/WP-IMPORT-SPEC.md). Everything a job may read, fetch or
// write is bounded here; the server reads these through kb/import/limits.js (tests may shrink
// them outside production).
export const KB_IMPORT_LIMITS = Object.freeze({
  ITEMS_MAX: 500,              // categories + articles one job may import (the KB's own cap is 500 too)
  CATEGORIES_MAX: 300,
  SCOPE_ITEMS_MAX: 500,        // hand-picked { type, id } items per job
  LOOKUP_MAX: 100,             // pasted addresses / ids one "find these pages" check resolves
  SCOPE_CATEGORIES_MAX: 50,    // WordPress categories per job
  EXCLUDE_MAX: 500,
  HTML_MAX_BYTES: 2_000_000,   // one item's content.rendered
  JSON_MAX_BYTES: 8_000_000,   // one REST response
  DISCOVERY_HTML_MAX_BYTES: 1_000_000,
  OEMBED_MAX_BYTES: 64 * 1024,
  DOM_NODES_MAX: 50_000,       // DOM nodes in one item's HTML
  IMAGES_PER_ARTICLE: 50,      // images + video posters in one article
  IMAGES_MAX: 2_000,           // downloads per job
  IMAGE_MAX_BYTES: 20 * 1024 * 1024,
  IMAGE_MAX_PX: 2_400,         // srcset: the largest candidate at most this wide
  BYTES_MAX: 1024 ** 3,        // image bytes per job
  RUN_MAX_MS: 60 * 60_000,     // one run (the worker stops after the current item)
  PLAN_MAX_MS: 15 * 60_000,    // one plan (discovery + listings + bodies + probes + oEmbed)
  REQUESTS_MAX: 3_000,         // REST/HTML/probe/oEmbed requests per job, retries included (images: IMAGES_MAX)
  DETECT_MAX_MS: 20_000,       // one site check (detect, MCP category slugs)
  URL_MAX: 2_048,              // a URL/slug taken from the site (longer → the item/link is left out)
  TERM_CHAIN_MAX: 10,          // parent hops walked up a WordPress category's ancestry
  ITEM_MAX_MS: 90_000,         // one article's images + write
  FETCH_TIMEOUT_MS: 15_000,    // one REST/HTML request (per hop)
  FETCH_RETRIES: 3,            // 429 / 503 / network, honouring Retry-After (≤ 60 s)
  HOST_GAP_MS: 150,            // between two JSON requests of one job
  IMAGE_CONCURRENCY: 3,
  REDIRECT_PROBES_MAX: 200,    // old-slug probes (headers only) per job
  RATE_LIMITED_MAX: 5,         // consecutive 429s before the job fails source_rate_limited
  PLAN_TTL_HOURS: 24,          // a previewed plan is runnable this long
  JOB_TTL_DAYS: 30,
  JOBS_PER_HOUR: 6,            // per workspace
  REPLANS_PER_HOUR: 20,        // per workspace: a preview re-made for the SAME address + selection (replace) — not JOBS_PER_HOUR
  EDITS_PER_HOUR: 60,          // per workspace: PATCH exclusions of a preview (leaf toggles — no site request)
  DETECT_PER_HOUR: 30,         // per workspace
  WARNINGS_PER_ITEM: 20,       // distinct warning codes kept per row
  MCP_PREVIEW_ROWS: 200,
  MCP_PREVIEW_WAIT_MS: 45_000,
  JOBS_LISTED: 10
});
// kbImports.state. planning → previewed → running → done | failed | cancelled.
export const KB_IMPORT_STATE = Object.freeze({
  PLANNING: 'planning', PREVIEWED: 'previewed', RUNNING: 'running', DONE: 'done', FAILED: 'failed', CANCELLED: 'cancelled'
});
// kbImports.results.items[].status.
export const KB_IMPORT_ITEM_STATUS = Object.freeze({
  CREATED: 'created', UPDATED: 'updated', UNCHANGED: 'unchanged', SKIPPED: 'skipped', FAILED: 'failed'
});
// kbImports.scope.kind — what the person explicitly chose to import. There is no "whole site".
export const KB_IMPORT_SCOPE_KIND = Object.freeze({ PAGE_TREE: 'page_tree', CATEGORIES: 'categories', ITEMS: 'items' });
// GET /api/kb/import/jobs/:id/redirects.<csv|txt>?format=
export const KB_IMPORT_REDIRECT_FORMATS = Object.freeze(['plain', 'redirection', 'htaccess', 'nginx']);
// Why a reader said an article wasn't helpful (optional; no free text by design).
export const KB_FEEDBACK_REASONS = Object.freeze(['unclear', 'outdated', 'missing', 'other']);
// Open Graph image canvas (the OG/Twitter large-card ratio).
export const KB_OG_IMAGE = Object.freeze({ WIDTH: 1200, HEIGHT: 630 });
// kbArticles.descriptionJob.state — the AI description worker's doc-as-queue states.
export const KB_DESCRIPTION_JOB = Object.freeze({
  PENDING: 'pending', DONE: 'done', ERROR: 'error', SKIPPED: 'skipped'
});
// Root-level slugs a category may not take — every one is (or will be) a real route on a
// tenant host, or a file the browser/crawlers request at the root.
export const KB_RESERVED_ROOT_SLUGS = Object.freeze([
  'api', 'auth', 'sso', 'email', 'login', 'logout', 'signin', 'signup', 'tickets', 'new', 'edit',
  'search', 'widget', 'assets', 'astro', 'oauth', 'mcp', 'well-known', 'robots', 'sitemap',
  'feed', 'rss', 'manifest', 'favicon', 'sw', 'push-sw', 'static', 'public', 'kb', 'tags', 'tag',
  'admin', 'settings', 'apple-touch-icon', 'essential-support-icon', 'essential-support-icon-masked'
]);
// Segments reserved at EVERY level (the agent edit/new shells hang off any path).
export const KB_RESERVED_SEGMENTS = Object.freeze(['edit', 'new']);
// Who may create/edit/publish KB content (owner setting, workspace.kb.editAccess.mode).
// Owners and admins always can; permanent deletes stay admin-only regardless.
export const KB_EDIT_ACCESS = Object.freeze({ ALL: 'all', ADMINS: 'admins', SELECTED: 'selected' });
// Who may import from WordPress (docs/kb-specs/WP-IMPORT-SPEC.md "P2 as built") — the minimum
// role on the agent < admin < owner ladder (roleCan). THE one switch: the REST routes
// (/api/kb/import/**), the jobs module, the worker's run-time re-check, the MCP tools (an AI
// app's grant imports only while its agent's role reaches it — grants are owners' anyway) and
// /api/me `capabilities.kbImport` (the Settings → Knowledge base → Import from WordPress tab)
// all read it. 'admin' opens importing to admins too.
export const KB_IMPORT_ACCESS = 'owner';
// Article byline (workspace.kb.byline.mode): every agent who edited the article, or one
// fixed name on every article.
export const KB_BYLINE = Object.freeze({ CONTRIBUTORS: 'contributors', FIXED: 'fixed' });
export const KB_DEFAULT_BYLINE_NAME = 'Staff Writer';
// Where instant answers (auto-answer) draw from: the imported help articles (es-kb-1
// snippets) or the hosted KB's published articles. Hosted is only possible while the KB is
// on, and is the default then.
export const AUTO_ANSWER_SOURCE = Object.freeze({ EXTERNAL: 'external', HOSTED: 'hosted' });

// ── Localization ────────────────────────────────────────────────────────────
// Selectable page languages (BCP-47 catalog locales) that are LIVE — `en` is the
// source + default + fallback. A locale joins this list only once its catalog has
// been generated + reviewed; detection + validation both key off it, so enabling a
// language is a one-line change here. Full target set: es, fr, de, pt-BR, it, ja,
// zh-Hans, ko. Regional variants (es-ES/es-419, fr-FR/fr-CA) collapse to one catalog
// each — the full browser region tag still drives Intl date/number formatting (the
// client's `formatLocale`). Only Simplified Chinese; Traditional falls back to English.
export const SUPPORTED_LOCALES = Object.freeze(['en', 'es', 'fr', 'de', 'pt-BR', 'it', 'ja', 'zh-Hans', 'ko']);

// Endonyms for the language switcher — each shown in its own language. Registry for
// every target locale; the switcher only lists those in SUPPORTED_LOCALES.
export const LOCALE_NAMES = Object.freeze({
  en: 'English', es: 'Español', fr: 'Français', de: 'Deutsch',
  'pt-BR': 'Português (Brasil)', it: 'Italiano', ja: '日本語', 'zh-Hans': '简体中文', ko: '한국어'
});

// Map any BCP-47 language tag to a live catalog locale, or null if none applies
// (caller falls back to `en`). Derived from SUPPORTED_LOCALES so enabling a locale
// there enables its detection too. Shared by client browser-detection and server
// Accept-Language / preference validation so both agree.
export function matchLocale(tag) {
  if (!tag || typeof tag !== 'string') return null;
  const lower = tag.trim().toLowerCase();
  if (!lower) return null;
  for (const loc of SUPPORTED_LOCALES) if (loc.toLowerCase() === lower) return loc; // exact
  const parts = lower.split('-');
  const base = parts[0];
  const sub = parts.slice(1); // subtags only — the base can also be 2 chars (e.g. 'zh')
  const script = sub.find((p) => p.length === 4); // e.g. 'hans' | 'hant'
  const region = sub.find((p) => p.length === 2); // e.g. 'br' | 'ca' | 'tw'
  if (base === 'zh') {
    if (script === 'hant' || ['tw', 'hk', 'mo'].includes(region)) return null; // Traditional → en
    return SUPPORTED_LOCALES.includes('zh-Hans') ? 'zh-Hans' : null;
  }
  if (base === 'pt') return SUPPORTED_LOCALES.includes('pt-BR') ? 'pt-BR' : null; // only Brazilian PT
  // Any live locale whose base language matches (region collapses to the base catalog).
  return SUPPORTED_LOCALES.find((loc) => loc.toLowerCase().split('-')[0] === base) || null;
}

// subdomainSlug reserved list (spec defaults + operational additions, decision D2).
// Self-serve signup makes this the SOLE anonymous gate, so it must also cover the
// hostnames this deployment itself uses under the root domain — the marketing site
// (get.<root>), the inbound-email MX (in.<root>), and mail-provider auth records —
// or a tenant slug would be shadowed by (or squat) real infrastructure, and its
// single-use tokens could be emailed to a host we don't control.
export const RESERVED_SLUGS = Object.freeze([
  'www', 'app', 'api', 'admin', 'mail', 'smtp', 'ftp', 'dashboard', 'status',
  'docs', 'help', 'support', 'staging', 'test',
  // operational additions
  'agent', 'agents', 'portal', 'my', 'sso', 'auth', 'login', 'signin', 'secure',
  'cdn', 'static', 'assets', 'email', 'links', 'dev', 'demo', 'billing',
  // this deployment's own hostnames + mail infrastructure under the root domain
  // (get.<root> = the marketing site, media.<root> = its media host)
  'get', 'media', 'in', 'mx', 'ns', 'ns1', 'ns2', 'imap', 'pop', 'webmail',
  'autodiscover', 'autoconfig', 'em', 'url', 'bounces', 'mailer', 'noreply',
  // integration playground console host (sandbox.<root>); the per-workspace sandbox
  // sub-workspaces themselves take the reserved `-sbx` SUFFIX (see slugError).
  'sandbox'
]);

// The reserved slugs an OPERATOR may grant to ONE workspace via a per-workspace
// override (platform console → Subdomains; server/src/slugs/rules.js). A strict subset
// of RESERVED_SLUGS: these are reserved as product NAMES (a squatter on help.<root>
// or support.<root> would pass as Essential Support), not because the app answers on
// them — no fixed host or mail flow uses them — so the right workspace (ES's own help
// desk) can hold one safely. (A name only works if the wildcard DNS still covers it: a
// specific record pointing it elsewhere wins, whatever is granted here.) Every other
// reserved slug is infrastructure (app/admin/sandbox hosts, api, mail, ns, cdn, …) and
// stays hard-reserved: never overridable, never usable by any workspace.
export const OVERRIDABLE_RESERVED_SLUGS = Object.freeze(['help', 'support', 'docs', 'status']);

export const COOKIE = Object.freeze({
  END_USER: 'es_sid',
  AGENT: 'es_agent_sid',
  PLATFORM_ADMIN: 'es_padmin_sid', // platform operator session (admin.<root> only, host-only)
  MFA_PENDING: 'es_mfa_pending', // short-lived: agent magic link confirmed, awaiting 2nd factor (no session)
  PLATFORM_MFA_PENDING: 'es_padmin_mfa_pending', // same, for a platform operator on admin.<root>
  SANDBOX: 'es_sbx_sid', // sandbox playground session (sandbox.<root> only, host-only)
  // MCP connector: the pending /oauth/authorize request id (10 min, HttpOnly, host-only,
  // Lax) so a signed-out owner lands back on the consent page after signing in. Opaque;
  // useless without an owner session. Cleared on the consent decision.
  OAUTH_REQUEST: 'es_oauth_req'
});

// Workspace lifecycle status. `active` is the default (provisionWorkspace).
// `suspended` is written by the platform admin console and enforced by the
// host-resolver (403s the tenant's hosts) + the inbound-email intake.
export const WORKSPACE_STATUS = Object.freeze({
  ACTIVE: 'active',
  SUSPENDED: 'suspended'
});

// Ticket-type color palette (soft, readable backgrounds — mirrors Recollect's
// tag palette). New types cycle through these; owners can also pick a custom hex.
export const TYPE_COLORS = Object.freeze([
  '#deeef8', '#dff6dc', '#fef5dd', '#f5dcd4', '#e6d9e3',
  '#ffe6ca', '#e3e5e8', '#d6e4ff', '#ffd9e0', '#d9f2ef'
]);

// Deterministic default so tests + a fresh workspace get distinct type colors
// without Math.random.
export function defaultTypeColor(index = 0) {
  return TYPE_COLORS[((index % TYPE_COLORS.length) + TYPE_COLORS.length) % TYPE_COLORS.length];
}

// Default ticket types seeded into every new workspace. Each carries a stable
// `key` so the client can localize the seeded name to the viewer's language
// (catalog `ticket:types.<key>`); the English `name` is the seed value + the
// fallback when a catalog is missing the key. Owner-created custom types have no
// key and always render exactly as authored. Keys are also the backfill target
// for workspaces provisioned before types carried a key (see mongo.js).
export const DEFAULT_TICKET_TYPES = Object.freeze([
  Object.freeze({ key: 'technical_issue', name: 'Technical Issue' }),
  Object.freeze({ key: 'setup', name: 'Setup' }),
  Object.freeze({ key: 'account', name: 'Account' })
]);

// ── Billing / plans (Stripe Team plan) ──────────────────────────────────────
// Shared so server (resolver + seat gate) and client (checkout plan param + seat
// display) reference the SAME identifiers and can never drift. The identifiers
// ARE the Stripe price lookup_keys — prices are resolved at call time by
// lookup_key (never a hard-coded price id), so a price can rotate in the Stripe
// dashboard with zero deploys. The keys are ES_-namespaced because ES shares ONE
// Stripe account with the sibling product Recollect (both are Coywolf LLC); Recollect
// already owns the bare `TEAM_MONTHLY`/`TEAM_YEARLY` lookup_keys, and Stripe allows
// only one active price per lookup_key per account. Money amounts are NOT here: the display prices
// live client-side in a TEAM_PRICES constant (the only marketing copy), and the
// authoritative amount is whatever the lookup_key resolves to at checkout.
//
// Packaging: FREE = 1 fully-featured agent. TEAM = a $10/mo or $90/yr base that
// INCLUDES 5 agents (1 owner + 4); each extra agent is a $2/mo or $18/yr seat
// add-on. "5 included" is expressed HERE (includedSeats), never in Stripe —
// Stripe bills a flat base plus N × seat-price, and maxAgents = includedSeats +
// purchased extra seats.
// Why an address is on a workspace's email-suppression list. Presence of a row is a
// HARD stop for all customer-recipient email (including sign-in links). `deleted` and
// `complained` are never self-clearable via the portal resume path.
// Presence of a suppression row stops mail — but the REASON decides how much:
//   unsubscribed → a soft opt-out from NOTIFICATIONS (reply + automated emails) only;
//                  sign-in/verify still send, so a one-click unsubscribe (which a mail
//                  security appliance may auto-trigger) can NEVER lock a customer out.
//   blocked      → an explicit "this isn't me / stop everything" — blocks ALL mail
//                  including sign-in links (the wrong-email-at-sign-in vector).
//   bounced / complained / deleted → hard stops (dead mailbox / spam report / erased).
export const SUPPRESSION_REASON = Object.freeze({
  UNSUBSCRIBED: 'unsubscribed',
  BLOCKED: 'blocked',
  COMPLAINED: 'complained',
  BOUNCED: 'bounced',
  DELETED: 'deleted'
});

// Reasons that stop EVERY customer email, including user-requested sign-in/verify.
// `unsubscribed` is deliberately absent — it silences notifications only.
export const HARD_SUPPRESSION_REASONS = Object.freeze(['blocked', 'complained', 'bounced', 'deleted']);

// Reasons a signed-in customer may self-clear (the portal "Resume emails" button):
// their own opt-out, a "not me" block they set, or a transient bounce — but NOT a spam
// complaint (they told us it's spam) or an account deletion (that's an erasure receipt).
export const RESUMABLE_SUPPRESSION_REASONS = Object.freeze(['unsubscribed', 'blocked', 'bounced']);

export const BILLING_TIER = Object.freeze({ FREE: 'free', TEAM: 'team' });

// A FREE workspace gets exactly one agent (the owner). A TEAM base bundles 5.
export const FREE_MAX_AGENTS = 1;
export const TEAM_INCLUDED_SEATS = 5;
// Hard cap on purchased extra seats (mirrors Recollect's add-on quantity clamp) —
// bounds a fat-fingered seat purchase and the seat stepper's upper end.
export const MAX_EXTRA_SEATS = 100;

// Plan table keyed by the Stripe price lookup_key of the subscription's BASE item.
// The resolver (server/src/billing/plan.js) copies the matching row and derives
// maxAgents for team plans from the seat-item quantities; these are the base
// values. FREE is the fallback for "no subscription" and for an unknown base
// lookup_key (degraded, never broken). Frozen: a spread copy is what the resolver
// mutates, so the shared table can't be corrupted by a stray write. Every field
// is a primitive, so a shallow spread is a full deep copy (unlike Recollect's
// nested maxRecords — ES has no record cap to nest).
export const PLANS = Object.freeze({
  FREE: Object.freeze({
    _id: 'FREE', name: 'Free', tier: BILLING_TIER.FREE,
    maxAgents: FREE_MAX_AGENTS, includedSeats: FREE_MAX_AGENTS, interval: null
  }),
  ES_TEAM_MONTHLY: Object.freeze({
    _id: 'ES_TEAM_MONTHLY', name: 'Team', tier: BILLING_TIER.TEAM,
    includedSeats: TEAM_INCLUDED_SEATS, interval: 'month'
  }),
  ES_TEAM_YEARLY: Object.freeze({
    _id: 'ES_TEAM_YEARLY', name: 'Team', tier: BILLING_TIER.TEAM,
    includedSeats: TEAM_INCLUDED_SEATS, interval: 'year'
  })
});

// Add-on seat lookup_keys — NEVER a base plan. The resolver sums their quantities
// into extraSeats; the seat item's cadence always matches the base (Stripe forbids
// mixed intervals on one subscription). ES_-namespaced for the shared Coywolf Stripe
// account (Recollect uses TEAM_MEMBER_* for its own seat add-on).
export const SEAT_KEYS = Object.freeze(['ES_TEAM_AGENT_MONTHLY', 'ES_TEAM_AGENT_YEARLY']);

// Only these base plans may START a Checkout. A seat lookup_key checked out as a
// base would resolve to the FREE fallback while billing real money — the allowlist
// closes that trap (mirror Recollect's CHECKOUT_PLANS).
export const CHECKOUT_PLANS = Object.freeze(['ES_TEAM_MONTHLY', 'ES_TEAM_YEARLY']);

// Stamped on every ES-created Stripe object (checkout session + subscription
// metadata) as `metadata.app`. In the shared Coywolf account the ES webhook endpoint
// also receives Recollect's checkout/subscription events; the marker lets ES tell its
// own events apart and ACK-ignore foreign ones instead of 404-retrying them for days.
export const STRIPE_APP_MARKER = 'essential-support';

// Branding defaults applied when a workspace hasn't customized them.
export const BRANDING_DEFAULTS = Object.freeze({
  logoUrl: null,
  logoMaxWidth: 200, // px; logo renders at a fixed height, this bounds width
  logoHeight: 32, // px
  headingColor: '#1a1a2e',
  linkColor: '#233f64' // default header background
});
